/* global document, window, HTMLElement */
import React, {
  ReactNode,
  SFC,
  SyntheticEvent,
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import SetWindowViewport from 'app/components/layout/SetWindowViewport';
import { GetModalCoordinator, RenderModalIfCurrent, ModalPriority } from 'app/components/modals/ModalCoordinator';
import { TAB_KEYCODE, ESC_KEYCODE } from 'app/constants/Keycodes';
import { useClasses } from 'app/utils/css';
import ElementScrollProvider from '../scroll/ElementScrollProvider';
import { useModalCoords } from 'app/components/modals/ModalCoordsContext';
import { useLatestCallback } from 'app/utils/react';

// This file is the main entry point for modals.
export {
  WithModalCoordinator,
  ModalPriority,
} from 'app/components/modals/ModalCoordinator';

export const MODAL_BORDER_RADIUS = 5;

function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

interface IModalContainerProps {
  children?: ReactNode;
  onOverlayClick?: () => void;
  disableAutoscroll: boolean;
  noBackground: boolean;
  shadeColor?: string;
  containerRef: (element: HTMLDivElement | null) => void;
}

const ModalWrapper: FunctionComponent<IModalContainerProps> = ({
  onOverlayClick,
  disableAutoscroll = false,
  noBackground,
  shadeColor = 'rgba(0, 0, 0, 0.65)',
  containerRef,
  children,
}) => {
  const modalCoords = useModalCoords();
  const [didFocusContainer, setDidFocusContainer] = useState(false);

  const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // When using ReactDOM.createPortal(), events are propagated according to the React tree, not
    // the DOM tree. If we don't stop propagation here, then the click event will bubble up to the
    // component that renders the <Modal>. This can cause issues if, for example, the <Modal> is
    // rendered inside of a button that opens the modal on click (because `onOverlayClick()` will
    // close the modal, but then the button will think it got clicked and open the modal again).
    event.stopPropagation();
    if (onOverlayClick) {
      onOverlayClick();
    }
  }, [onOverlayClick]);

  const getRef = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      element.focus();
      setDidFocusContainer(true);
    }

    containerRef(element);
  }, [containerRef, setDidFocusContainer]);

  const scrollable = !disableAutoscroll;
  const { top, bottom, left, right } = modalCoords;

  const classes = useClasses({
    modal: {
      position: 'fixed',
      top: `${top}px`,
      bottom: `${bottom}px`,
      left: `${left}px`,
      right: `${right}px`,
      zIndex: 1,
      overflowX: 'hidden',
      overflowY: scrollable ? 'auto' : 'hidden',
      ':focus': {
        outline: 0,
      },
    },
    container: [
      {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        alignItems: 'center',
      },
      scrollable ? { minHeight: '100%' } : { height: '100%' },
    ],
    content: [
      {
        margin: '10px',
        maxWidth: '100%',
        flexGrow: 0,
        flexShrink: 1,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1,
      },
      !noBackground && {
        backgroundColor: '#fff',
        boxShadow: '0px 1px 16px rgba(0, 0, 0, 0.4)',
        borderRadius: `${MODAL_BORDER_RADIUS}px`,
      },
    ],
  });

  return (
    <div
      data-testid="modal"
      className={classes.modal}
      style={{ backgroundColor: shadeColor }}
      onClick={onClick}
      role="dialog"
      aria-modal="true"
      tabIndex={0}
      ref={getRef}
    >
      <div className={classes.container}>
        <div
          className={classes.content}
          onClick={stopPropagation}
        >
          {/* Focus container before rendering content,
            in case content has auto-focusing logic as well. */}
          {didFocusContainer ? children : null}
        </div>
      </div>
    </div>
  );
};

interface IModalProps {
  children: ReactNode;
  onClose?: () => void;
  disableCloseOnEscape?: boolean;
  disableAutoscroll?: boolean;
  noBackground?: boolean;
  shadeColor?: string;
}

const Modal: FunctionComponent<IModalProps> = ({
  onClose,
  disableCloseOnEscape = false,
  disableAutoscroll = false,
  noBackground = false,
  shadeColor,
  children,
}) => {
  const [containerMounted, setContainerMounted] = useState(false);
  const containerNode = useRef<HTMLDivElement>(document.createElement('div'));

  const onCloseRequest = useLatestCallback(() => {
    if (!disableCloseOnEscape && onClose) {
      onClose();
    }
  });

  useEffect(() => {
    document.body.appendChild(containerNode.current);
    const lastFocused = document.activeElement ? document.activeElement : undefined;
    const currContainer = containerNode.current;
    // We want to only render the modal after the container node has been added to the DOM, so
    // children of `<Modal>` can accurately measure their dimensions and styles in
    // `componentDidMount`, but we don't want to cause side effects (like mutating the DOM) in the
    // constructor, so in this case we have to use setState in componentDidMount.
    //
    // See the comment in the `componentDidMount` method of the example `Modal` component in the
    // React docs for `createPortal`: https://reactjs.org/docs/portals.html
    setContainerMounted(true);

    const getFirstAndLastFocusables = () => {
      const focusables = currContainer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

      if (focusables.length > 0) {
        const firstItem: Element = focusables[0];
        const lastItem: Element = focusables[focusables.length - 1];
        if (firstItem instanceof HTMLElement && lastItem instanceof HTMLElement) {
          return { firstItem, lastItem };
        }
      }
      return null;
    };

    // Listen to keypress event and call onClose if disableCloseOnEscape is ever set.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === ESC_KEYCODE) {
        onCloseRequest();
      }

      // Trap focus inside modal (tabbing after the last element returns to first element)
      if (e.keyCode === TAB_KEYCODE) {
        const focusables = getFirstAndLastFocusables();
        if (focusables) {
          if (e.shiftKey) {
            if (document.activeElement === focusables.firstItem) {
              focusables.lastItem.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === focusables.lastItem) {
              focusables.firstItem.focus();
              e.preventDefault();
            }
          }
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);

      document.body.removeChild(currContainer!);
      if (lastFocused instanceof HTMLElement) {
        lastFocused.focus();
      }
    };
  }, [onCloseRequest]);

  if (containerMounted) {
    return ReactDOM.createPortal(
      <SetWindowViewport>
        <ElementScrollProvider>{(ref) =>
          <ModalWrapper
            onOverlayClick={onClose}
            disableAutoscroll={disableAutoscroll!} // has default
            noBackground={noBackground!}           // has default
            shadeColor={shadeColor}
            containerRef={ref}
          >
            {children}
          </ModalWrapper>
        }</ElementScrollProvider>
      </SetWindowViewport>,
      containerNode.current!,
    );
  } else {
    // On the first render, before componentDidMount is called, the container node for the portal
    // will not be mounted into the DOM, so just render nothing.
    return null;
  }
};

interface ISingletonModalProps extends IModalProps {
  priority?: ModalPriority;
}

const SingletonModal: SFC<ISingletonModalProps> = ({ priority, ...props }) => (
  <GetModalCoordinator>{(modalCoordinator) => {
    // Fallback behaviour: if a modalCoordinator is not present the
    // current modal is assumed to be the only one.
    if (!modalCoordinator) {
      return <Modal {...props} />;
    }

    return (
      <RenderModalIfCurrent
        modalCoordinator={modalCoordinator}
        priority={priority}
      >
        <Modal {...props} />
      </RenderModalIfCurrent>
    );
  }
  }</GetModalCoordinator>
);

export default SingletonModal;
